import { pickBy } from 'lodash'
import { Annotations, Shape } from 'plotly.js'
import { ColumnTable } from 'arquero'
import {
  CellInfoGroups,
  CellPlotData,
  CellPlotDatum,
  PinnedMorphotype,
  ScatterPlotMetadata,
} from 'redux/slices'
import { CellInfo } from '../tsv/types'
import { getColumn } from '../arrow/arqueroHelpers'
import { BasePlotParams, Range } from './types'

type FixedAnnotationParams = {
  fixed?: 'x' | 'y' | 'x+y' | undefined
  text: string
  x: Annotations['x']
  y: Annotations['y']
  angle?: number
}

type BannerParams = {
  /**
   * Location of the top of the banner as a percentage between 0 and 1 (1 being at the top of the plot, 0 being at the bottom)
   */
  top: number
  /**
   * Location of the bottom of the banner as a percentage between 0 and 1 (1 being at the top of the plot, 0 being at the bottom)
   */
  bottom: number
  /**
   * Location of the left of the banner as a percentage between 0 and 1 (1 being at the right of the plot, 0 being at the left)
   */
  left: number
  /**
   * Location of the right of the banner as a percentage between 0 and 1 (1 being at the right of the plot, 0 being at the left)
   */
  right: number
  /**
   * CSS color of the banner
   */
  fillcolor: string
}

export const getCellId = (cell: CellInfo): string =>
  cell.CELL_ID || `${cell.RUN_ID}_${cell.INSTRUMENT_ID}_${cell.CELL_NUMBER}`

export const addToExistingPlot = (
  plot: CellPlotData,
  x: number,
  y: number,
  originalX: number,
  originalY: number,
  cellId: string,
  color: string
): void => {
  plot.x.push(x)
  plot.y.push(y)
  plot.originalX.push(originalX)
  plot.originalY.push(originalY)
  plot.cellIds.push(cellId)
  plot.plotDatum.push({ x, y, cellId })

  if (plot.marker) {
    const colors = plot.marker.color as string[]
    colors.push(color)
  }
}

export const addNewPlotToData = (
  data: CellPlotData[],
  basePlotFunction: (params: BasePlotParams) => CellPlotData,
  basePlotParams: BasePlotParams
): void => {
  data.push(basePlotFunction(basePlotParams))
}

/**
 * DataFrame version - Retrieves fixed data about cell information that does not change.
 * Memory-efficient: operates directly on DataFrame columns instead of creating object arrays
 */
export const getMetadataFromDataFrame = (
  dataFrame: ColumnTable,
  exludeMorphotypes?: PinnedMorphotype[]
): ScatterPlotMetadata => {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  const allX = [] as number[]
  const allY = [] as number[]
  const allCellIds = [] as string[]
  const plotDatum = [] as CellPlotDatum[]

  let coordsToExclude
  if (exludeMorphotypes) {
    coordsToExclude = new Set<string>(
      exludeMorphotypes?.flatMap((m) => m.cells?.points?.flatMap((p) => `${p.x}${p.y}`) ?? [])
    )
  }

  // Extract columns from DataFrame using cached helper
  const cellIdColumn = getColumn<string>(dataFrame, 'CELL_ID')
  const umapXColumn = getColumn<number>(dataFrame, 'UMAP_0')
  const umapYColumn = getColumn<number>(dataFrame, 'UMAP_1')

  if (cellIdColumn.length === 0 || umapXColumn.length === 0 || umapYColumn.length === 0) {
    return {
      minX: 0,
      minY: 0,
      maxX: 0,
      maxY: 0,
      x: [],
      y: [],
      cellIds: [],
      plotDatum: [],
    }
  }

  for (let i = 0; i < cellIdColumn.length; i += 1) {
    const x = +umapXColumn[i]
    const y = +umapYColumn[i]
    const include = coordsToExclude ? !coordsToExclude.has(`${x}${y}`) : true

    if (include) {
      const cellId = cellIdColumn[i]

      minX = x < minX ? x : minX
      minY = y < minY ? y : minY
      maxX = x > maxX ? x : maxX
      maxY = y > maxY ? y : maxY

      const currentCellPlatDatum = { x, y, cellId }

      allX.push(x)
      allY.push(y)
      allCellIds.push(cellId)
      plotDatum.push(currentCellPlatDatum)
    }
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    x: allX,
    y: allY,
    cellIds: allCellIds,
    plotDatum,
  }
}

const defaultLineProps: Partial<Shape> = {
  type: 'line',
  line: {
    color: 'lightgrey',
    width: 2,
  },
}

type GridLine = {
  orientation: 'vertical' | 'horizontal'
  /**
   * Either x or y location, depending on orientation
   */
  location: number
}

/**
 * Returns grid line that spans the whole length of the plot, either vertically or horizontally
 */
export const getFullGridLine = ({ orientation, location }: GridLine): Partial<Shape> => {
  const isVert = orientation === 'vertical'

  return {
    ...defaultLineProps,
    name: `grid-line=${location}`,
    yref: isVert ? 'paper' : undefined,
    xref: isVert ? undefined : 'paper',
    x0: isVert ? location : 0,
    y0: isVert ? 0 : location,
    y1: isVert ? 1 : location,
    x1: isVert ? location : 1,
  } as Partial<Shape>
}

/**
 * Displays banner
 */
export const getBanner = ({
  top,
  bottom,
  left,
  right,
  fillcolor = 'lightblue',
}: BannerParams): Partial<Shape> => {
  return {
    name: 'banner',
    type: 'rect',
    xref: 'paper',
    yref: 'paper',
    x0: left,
    y0: top,
    x1: right,
    y1: bottom,
    fillcolor,
    line: { width: 0 },
    layer: 'above',
  } as Partial<Shape>
}

/**
 * Displays full width banner
 */
export const getHorizontalBanner = (
  params: Omit<BannerParams, 'left' | 'right'>
): Partial<Shape> => {
  return {
    ...getBanner({ ...params, left: 0, right: 1 }),
  }
}

/**
 * Displays full height banner
 */
export const getVerticalBanner = (params: Omit<BannerParams, 'top' | 'bottom'>): Partial<Shape> => {
  return {
    ...getBanner({ ...params, top: 1, bottom: 0 }),
  }
}

const asMultilineAnnotation = (text: string, charLimitPerLine = 30, delimeter = ' '): string => {
  let formattedText = ''

  const words = text.split(delimeter)
  let line = ''
  if (words.length > 1) {
    words.forEach((word) => {
      line += `${word} `
      if (line.length > charLimitPerLine) {
        // annotations are vertically aligned to the center, so offset any suffixed newlines with prefixed newlines
        // that little space at the beginning is needed because plotly :)
        formattedText = ` <br>${formattedText}${line}<br>`
        line = ''
      }
    })
    if (line) formattedText += line

    const trailingNewline = /<br>$/
    if (formattedText.match(trailingNewline)) {
      // if there's a trailing newline, remove it and the first newline
      // this will keep every annotation lined up
      formattedText = formattedText.replace(trailingNewline, '')
      formattedText = formattedText.replace(/<br>/, '')
    }
  } else {
    // No delimeters found
    formattedText = text
  }

  return formattedText
}

/**
 * Returns an annotation in a fixed spot
 */
export const getFixedAnnotation = ({
  fixed,
  text,
  x,
  y,
  angle,
}: FixedAnnotationParams): Partial<Annotations> => {
  const charLimitPerLine = 30
  let formattedText = text
  if (text.length > charLimitPerLine) {
    formattedText = asMultilineAnnotation(text, charLimitPerLine)
  }

  return {
    yref: fixed === 'y' || fixed === 'x+y' ? 'paper' : undefined,
    xref: fixed === 'x' || fixed === 'x+y' ? 'paper' : undefined,
    x,
    y,
    text: formattedText,
    xanchor: 'center',
    yanchor: 'middle',
    showarrow: false,
    font: { size: 15 },
    textangle: `${angle}`,
    align: 'center',
  } as Partial<Annotations>
}

/**
 * Return all non-continuous cell info groups
 */
export const nonContinuous = (cig: CellInfoGroups): CellInfoGroups =>
  pickBy(cig, (e) => !e.isContinuous)


/**
 * Calculates statistical range including quartiles for continuous field values
 * @param values Array of string or number values to analyze
 * @returns Range object with min, max, q1, q3, and median or undefined if no valid values
 *
 * min and max exclude outliers and are rounded values that look good on a colorbar.
 */
export const calculateRangeForContinuousField = (values: (string | number)[]): Range | undefined => {
  const numericValues = values
    .map(v => typeof v === 'string' ? parseFloat(v) : v)
    .filter(v => !Number.isNaN(v))
    .sort((a, b) => a - b)

  if (numericValues.length === 0) return undefined
  const q1Index = Math.floor((numericValues.length - 1) * 0.25)
  const q3Index = Math.floor((numericValues.length - 1) * 0.75)
  const medianIndex = Math.floor((numericValues.length - 1) * 0.5)

  const q1 = numericValues[q1Index]
  const q3 = numericValues[q3Index]
  const median = numericValues[medianIndex]
  const iqr = q3 - q1
  const lowerBound = q1 - (1.5 * iqr)
  const upperBound = q3 + (1.5 * iqr)
  // Filter values within IQR bounds
  const filteredValues = numericValues.filter(v => v >= lowerBound && v <= upperBound)

  // Min and max values excluding outliers
  let min = filteredValues[0]
  let max = filteredValues[filteredValues.length - 1]

  // Round min and max values so that they get a nice place on the colorbar
  const scale = 10 ** (Math.floor(Math.log10(max - min)) - 1)
  min = Math.floor(min / scale) * scale
  max = Math.ceil(max / scale) * scale

  return {
    min,
    max,
    q1,
    q3,
    median
  }
}

export const calculatePlotRangeWithPadding = (data: CellPlotData[]): { rangeX: [number, number]; rangeY: [number, number] } => {
  const xValues = data.flatMap((trace) => (Array.isArray(trace.x) ? trace.x : []));
  const yValues = data.flatMap((trace) => (Array.isArray(trace.y) ? trace.y : []));

  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  const paddingX = (maxX - minX) * 0.3;
  const paddingY = (maxY - minY) * 0.3;

  return {
    rangeX: [minX - paddingX, maxX + paddingX],
    rangeY: [minY - paddingY, maxY + paddingY]
  }
}
